import { Fetcher } from 'swr';

import { deepCamelCase, NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';

import { ShowcaseAuthResponse } from '../types/types';

/**
 * Retrieves the referrer value for the current document.
 *
 * This function attempts to obtain the referrer information in the following order:
 * 1. Returns `document.referrer` if available.
 * 2. If not, attempts to retrieve the content of a `<meta name="referrer">` tag.
 * 3. If neither is available or an error occurs, returns an empty string.
 *
 * @returns {string} The referrer value, or an empty string if not available.
 */
export const getReferrerValue = () => {
  try {
    if (document?.referrer) {
      return document.referrer;
    }

    return document?.querySelector('meta[name="referrer"]')?.getAttribute('content') || '';
  } catch (error) {
    return '';
  }
};

/**
 * Retrieves the value of the 'content' attribute from a meta tag in the DOM
 * that matches the specified `name` attribute.
 *
 * @param key - The value of the `name` attribute to search for in meta tags.
 * @returns The value of the `content` attribute if the meta tag is found; otherwise, `null` or `undefined`.
 */
export const getSSGMetaDataFromDOM = (key: string) =>
  document.querySelector(`meta[name=${key}]`)?.getAttribute('content');

/**
 * Serializes an object of query parameters into a URL query string.
 *
 * @param options - An object containing key-value pairs to be serialized as query parameters.
 *                  Keys with `undefined` values are omitted from the result.
 * @returns A string representing the serialized query parameters, prefixed with `?` if any parameters exist,
 *          or an empty string if no valid parameters are present.
 * ```
 */
export const serializeQuery = (options: Record<string, unknown>) => {
  const res = Object.entries(options)
    .filter(([, value]) => typeof value !== 'undefined')
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return res ? `?${res}` : '';
};

/**
 * Fetches data from the given URL and returns the response in camelCase format.
 * Handles network errors and 404 responses by throwing a `NetworkError`.
 *
 * @template ShowcaseAuthResponse - The expected response type.
 * @param url - The URL to fetch data from.
 * @returns A promise that resolves to the camelCased JSON response.
 * @throws {NetworkError} If the response status is 404 or if any network error occurs.
 */
export const showcaseAuthFetcher: Fetcher<ShowcaseAuthResponse, string> = async (url) => {
  const res = await fetch(url);

  if (res.status === 404) {
    throw new NetworkError('Not found', res.status, res);
  }

  if (!res.ok) {
    const errorRes = await res.json();

    throw new NetworkError('A network error occurred', res.status, deepCamelCase(errorRes));
  }

  const json = await res.json();

  return deepCamelCase(json);
};

/**
 * Returns a `URLSearchParams` instance for the current window's search parameters,
 * or a mock object with a `get` method if executed in a non-browser environment (e.g., server-side).
 *
 * @returns {URLSearchParams | { get: (name: string) => string }}
 * Returns a `URLSearchParams` object if `window` is defined, otherwise returns an object
 * with a `get` method that logs a message and returns an empty string.
 */
export const getSearchParams = () => {
  if (typeof window === 'undefined') {
    return {
      get: () => {
        console.log('window undefined');
        return '';
      },
    };
  }
  return new URLSearchParams(window.location.search);
};
